import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, poundsFromPence } from "../lib/api";
import { useAttraction } from "../lib/attraction";
import DashboardLayout from "../components/DashboardLayout";
import { Ban, CheckCircle2, Link2, ExternalLink } from "lucide-react";
import { promptDialog } from "../components/ConfirmDialog";
import { notify } from "../lib/notify";

export default function EventDetailPage() {
  const { id } = useParams();
  const { attraction } = useAttraction() || {};
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [slug, setSlug] = useState("");
  const [savingSlug, setSavingSlug] = useState(false);

  const load = async () => {
    try { const r = await api.get(`/events/${id}`); setData(r.data); setSlug(r.data.event.slug || ""); }
    catch (e) { setErr("Could not load event"); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, [id]);

  const saveSlug = async () => {
    setSavingSlug(true);
    try {
      const r = await api.patch(`/events/${id}`, { slug });
      setSlug(r.data.slug);
      setData((d) => ({ ...d, event: { ...d.event, slug: r.data.slug } }));
      notify.success("Public link updated");
    } catch (e) { notify.error(e?.response?.data?.detail || "Could not update link"); }
    finally { setSavingSlug(false); }
  };

  const block = async (dateId, reason) => { await api.post(`/events/${id}/dates/${dateId}/block`, { reason }); load(); };
  const unblock = async (dateId) => { await api.post(`/events/${id}/dates/${dateId}/unblock`); load(); };

  if (err) return <DashboardLayout title="Event"><div className="text-[#fca5a5]">{err}</div></DashboardLayout>;
  if (!data) return <DashboardLayout title="Loading…" />;

  const aSlug = attraction?.slug;
  const fullUrl = aSlug ? `${window.location.origin}/${aSlug}/${data.event.slug}` : null;

  return (
    <DashboardLayout title={data.event.name} subtitle={data.event.description}>
      <div className="st-card p-4 mb-6" data-testid="event-link-card">
        <div className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] flex items-center gap-2 mb-3">
          <Link2 size={14} /> Public link
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[260px]">
            <label className="text-xs text-[#8a8a8a]">Event URL slug</label>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-sm text-[#7a7a7a] whitespace-nowrap">{aSlug ? `/${aSlug}/` : "/"}</span>
              <input className="st-input flex-1" value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="event-slug-input" />
            </div>
          </div>
          <button className="st-btn" onClick={saveSlug} disabled={savingSlug || !slug || slug === data.event.slug} data-testid="event-slug-save">
            {savingSlug ? "Saving…" : "Save link"}
          </button>
          {fullUrl && (
            <a href={fullUrl} target="_blank" rel="noreferrer" className="st-btn st-btn-ghost" data-testid="event-link-open">
              <ExternalLink size={14} /> View page
            </a>
          )}
        </div>
        {fullUrl && <div className="text-xs text-[#7a7a7a] mt-2 break-all">{fullUrl}</div>}
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <Mini label="Ticket types" value={data.ticket_types.length} />
        <Mini label="Dates" value={data.dates.length} />
        <Mini label="Add-ons" value={data.addons.length} />
      </div>

      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Ticket types</h3>
      <div className="st-card overflow-hidden mb-6">
        <table className="w-full text-sm">
          <thead className="text-left text-[10px] uppercase tracking-wider text-[#7a7a7a]">
            <tr><th className="px-4 py-3">Name</th><th>Base price</th><th>Max/slot</th><th>Tiers</th></tr>
          </thead>
          <tbody>
            {data.ticket_types.map((t) => (
              <tr key={t.id} className="border-t border-[#1a1a1a]">
                <td className="px-4 py-3">{t.name}</td>
                <td>{poundsFromPence(t.base_price_pence)}</td>
                <td>{t.max_per_slot ?? "—"}</td>
                <td>{(t.tiers || []).length}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h3 className="text-sm uppercase tracking-[0.18em] text-[#8a8a8a] mb-3">Dates</h3>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dates-grid">
        {data.dates.map((d) => (
          <div key={d.id} className="st-card p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-semibold">{d.date}</div>
                <div className="text-xs text-[#8a8a8a]">{d.open_time}-{d.close_time} · cap {d.max_capacity}</div>
              </div>
              <div className="flex items-center gap-2">
                {d.status === "cancelled" && <span className="st-chip st-chip-red">Cancelled</span>}
                {d.is_blocked && <span className="st-chip st-chip-amber">Blocked</span>}
                {!d.is_blocked && d.status === "active" && <span className="st-chip st-chip-green">Active</span>}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              {d.is_blocked ? (
                <button className="st-btn st-btn-ghost text-xs" onClick={() => unblock(d.id)} data-testid={`unblock-${d.id}`}>
                  <CheckCircle2 size={12} /> Unblock
                </button>
              ) : (
                <button className="st-btn st-btn-ghost text-xs" onClick={async () => {
                  const r = await promptDialog({
                    title: "Block this date?",
                    description: "Customers won't be able to book new tickets. Existing orders are unaffected.",
                    promptLabel: "Reason (optional, shown internally)",
                    confirmLabel: "Block date",
                  });
                  if (r != null) block(d.id, r);
                }} data-testid={`block-${d.id}`}>
                  <Ban size={12} /> Block
                </button>
              )}
            </div>
            {d.time_slots && d.time_slots.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1">
                {d.time_slots.slice(0, 12).map(s => (
                  <span key={s.id} className="text-[10px] px-2 py-0.5 rounded bg-[#161616] border border-[#222] text-[#aaa]">{s.start_time}</span>
                ))}
                {d.time_slots.length > 12 && <span className="text-[10px] text-[#8a8a8a]">+{d.time_slots.length - 12}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
    </DashboardLayout>
  );
}

function Mini({ label, value }) {
  return (
    <div className="st-card p-4">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#8a8a8a]">{label}</div>
      <div className="text-2xl font-bold mt-2">{value}</div>
    </div>
  );
}
